import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { MdAlternateEmail, MdSend, MdSecurity } from "react-icons/md";
import { UsersApi } from "../../../../api/common/apiIndex";
import { isValidEmail } from "../../../../utils/validators";

const buildError = (error) =>
  error?.response?.data?.message ||
  error?.response?.data ||
  error?.message ||
  "Failed to send invite.";

const ROLE_OPTIONS = [
  {
    value: "ADMIN",
    label: "Admin",
    description: "Manage the business, team members, and key operations.",
  },
  {
    value: "AGENT",
    label: "Agent",
    description: "Handle day-to-day field operations with limited permissions.",
  },
];

export default function InviteAgentForm() {
  const [email, setEmail] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [touched, setTouched] = useState(false);
  const [confirmTouched, setConfirmTouched] = useState(false);
  const [roleTouched, setRoleTouched] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState(() => new Set(["AGENT"]));

  const inviteUser = useMutation({
    mutationFn: ({ email: targetEmail, roles }) => UsersApi.inviteUser({ email: targetEmail, roles }),
    onSuccess: () => {
      toast.success("Invite sent. The user will receive an email shortly.");
      setEmail("");
      setConfirmEmail("");
      setTouched(false);
      setConfirmTouched(false);
      setRoleTouched(false);
      setSelectedRoles(new Set(["AGENT"]));
    },
    onError: (error) => {
      toast.error(buildError(error));
    },
  });

  const trimmed = email?.trim() || "";
  const confirmTrimmed = confirmEmail?.trim() || "";
  const emailValid = trimmed !== "" && isValidEmail(trimmed);
  const confirmValid = confirmTrimmed !== "" && isValidEmail(confirmTrimmed);
  const emailsMatch = emailValid && confirmValid && trimmed === confirmTrimmed;
  const invalidEmail = touched && !emailValid;
  const confirmMismatch = confirmTouched && confirmTrimmed !== "" && trimmed !== "" && trimmed !== confirmTrimmed;
  const invalidConfirm =
    confirmTouched &&
    (!confirmValid || confirmTrimmed === "" || confirmMismatch);
  const showValidState = emailsMatch;
  const rolesArray = Array.from(selectedRoles);
  const invalidRoles = roleTouched && rolesArray.length === 0;

  const toggleRole = (value) => {
    setSelectedRoles((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setTouched(true);
    setConfirmTouched(true);
    setRoleTouched(true);
    if (!emailValid || !confirmValid || !emailsMatch || inviteUser.isPending || rolesArray.length === 0) return;
    inviteUser.mutate({ email: trimmed, roles: rolesArray });
  };

  return (
    <div className="card border-0 shadow-sm rounded-4 h-100">
      <div className="card-body p-3 p-md-4">
        <div className="mb-3">
          <h2 className="h5 mb-1">Invite an agent</h2>
          <p className="text-muted mb-0">
            Send a secure email so the team member can complete their own profile and password.
          </p>
        </div>

        <form onSubmit={handleSubmit} noValidate>
          <label className="form-label" htmlFor="invite-email">
            Agent email
          </label>
          <div className="input-group">
            <span className="input-group-text bg-body">
              <MdAlternateEmail />
            </span>
            <input
              id="invite-email"
              type="email"
              className={`form-control ${invalidEmail ? "is-invalid" : ""} ${showValidState ? "is-valid" : ""}`}
              placeholder="name@example.com"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              onBlur={() => setTouched(true)}
              autoComplete="email"
              disabled={inviteUser.isPending}
              required
            />
          </div>
          {invalidEmail && (
            <div className="invalid-feedback d-block">
              Please enter a valid email address.
            </div>
          )}

          <label className="form-label mt-3" htmlFor="invite-confirm-email">
            Confirm email
          </label>
          <div className="input-group">
            <span className="input-group-text bg-body">
              <MdAlternateEmail />
            </span>
            <input
              id="invite-confirm-email"
              type="email"
              className={`form-control ${invalidConfirm || confirmMismatch ? "is-invalid" : ""} ${showValidState ? "is-valid" : ""}`}
              placeholder="Re-enter email"
              value={confirmEmail}
              onChange={(event) => setConfirmEmail(event.target.value)}
              onBlur={() => setConfirmTouched(true)}
              autoComplete="email"
              disabled={inviteUser.isPending}
              required
            />
          </div>
          {confirmMismatch && (
            <div className="invalid-feedback d-block">Email addresses do not match.</div>
          )}
          {!confirmMismatch && invalidConfirm && (
            <div className="invalid-feedback d-block">Please enter a valid email address.</div>
          )}

          <div className="mt-4">
            <label className="form-label d-flex align-items-center gap-2">
              <MdSecurity /> Roles
            </label>
            <div className="d-flex flex-column gap-2">
              {ROLE_OPTIONS.map((role) => {
                const checked = selectedRoles.has(role.value);
                const roleId = `invite-role-${role.value}`;
                return (
                  <div
                    key={role.value}
                    className={`border rounded-4 p-3 d-flex flex-column gap-1 ${checked ? "bg-body-tertiary" : ""}`}
                  >
                    <div className="form-check mb-1">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id={roleId}
                        checked={checked}
                        onChange={() => toggleRole(role.value)}
                        onBlur={() => setRoleTouched(true)}
                        disabled={inviteUser.isPending}
                      />
                      <label className="form-check-label ms-2 fw-semibold" htmlFor={roleId}>
                        {role.label}
                      </label>
                    </div>
                    <small className="text-body-secondary">{role.description}</small>
                  </div>
                );
              })}
            </div>
            {invalidRoles && (
              <div className="invalid-feedback d-block">
                Select at least one role to assign with the invite.
              </div>
            )}
          </div>

          <div className="d-flex justify-content-end mt-3">
            <button
              type="submit"
              className="btn btn-primary rounded-pill d-flex align-items-center gap-2"
              disabled={inviteUser.isPending}
            >
              {inviteUser.isPending && (
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
              )}
              Send invite
              <MdSend />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
